import { iamRequest, iamBaseUrl } from '../redux/utils/iamRequest';
import { isOpenKnotsEnvEnabled } from '../config/openKnotsFeature';

const parseEnabled = (payload, snakeKey) =>
    !!(payload?.enabled ?? payload?.[snakeKey]);

const getOrgFromUserData = (userData) => userData?.user_organizations?.[0];

const getPlatformRole = (userData) =>
    getOrgFromUserData(userData)?.user_org_role_maps?.[0]?.platform_role;

/** APPLICATIO-2912: Trade Support Program (deal knots) is not offered on Broker. */
export function isDealKnotsAllowedForRole(role) {
    return role !== 'broker';
}

export function getBrokerKnotVisibility(flagResults) {
    return {
        trade: !!(flagResults?.trade?.ok && flagResults.trade.enabled),
        deal: false,
        openKnots: false,
    };
}

/** IAM GET flags used by route/transaction gates — deal always off for brokers. */
export function applyRoleIamFlagOverrides(flags, role) {
    if (!flags || isDealKnotsAllowedForRole(role)) {
        return flags;
    }
    return { ...flags, deal: { ok: true, enabled: false } };
}

/** Profile fallback when IAM GET fails (401/404) or endpoints are not deployed yet.
 * Fail-closed: missing flags → product OFF (explicit true required). */
export function getKnotVisibilityFromProfile(userData) {
    const role = getPlatformRole(userData);
    const org = getOrgFromUserData(userData);

    if (role !== 'buyer' && role !== 'supplier') return null;

    return {
        trade: org?.trade_knots_enabled === true,
        deal: org?.deal_knots_enabled === true,
        openKnots: isOpenKnotsEnvEnabled() && org?.open_knots_enabled === true,
    };
}

/** @deprecated Use getKnotVisibilityFromProfile */
export function getBuyerKnotVisibilityFromProfile(userData) {
    return getKnotVisibilityFromProfile(userData);
}

/** GET trade / deal / open-knots IAM flags for an org (parallel). */
export async function fetchOrgKnotFlags(
    organizationUlid,
    { includeOpenKnots = true, includeDeal = true } = {},
) {
    const base = iamBaseUrl();
    const get = async (path, snakeKey) => {
        try {
            const r = await iamRequest({
                method: 'get',
                url: `${base}/iam/${organizationUlid}/${path}`,
                skipGlobalAuthLogout: true,
            });
            return { ok: true, enabled: parseEnabled(r.payload, snakeKey) };
        } catch {
            return { ok: false, enabled: false };
        }
    };

    const requests = [get('trade-knots', 'trade_knots_enabled')];
    if (includeDeal) {
        requests.push(get('deal-knots', 'deal_knots_enabled'));
    }
    if (includeOpenKnots) {
        requests.push(get('open-knots', 'open_knots_enabled'));
    }

    const results = await Promise.all(requests);
    let i = 0;
    const trade = results[i++];
    const deal = includeDeal ? results[i++] : { ok: true, enabled: false };
    const openKnots = includeOpenKnots ? results[i] : { ok: true, enabled: false };
    return { trade, deal, openKnots };
}

/** @deprecated Use fetchOrgKnotFlags */
export async function fetchBuyerKnotFlags(organizationUlid) {
    return fetchOrgKnotFlags(organizationUlid, { includeOpenKnots: true });
}

/** Map raw IAM flag results to tab visibility, with profile fallback per failed GET. */
export function getKnotVisibility(flagResults, userData) {
    const role = getPlatformRole(userData);
    const profile = getKnotVisibilityFromProfile(userData);
    const pick = (key) => {
        const result = flagResults?.[key];
        if (result?.ok) {
            if (key === 'openKnots') {
                return (role === 'buyer' || role === 'supplier') && isOpenKnotsEnvEnabled() && !!result.enabled;
            }
            return !!result.enabled;
        }
        return profile?.[key] ?? false;
    };

    return {
        trade: pick('trade'),
        deal: isDealKnotsAllowedForRole(role) && pick('deal'),
        openKnots: pick('openKnots'),
    };
}

/** @deprecated Use getKnotVisibility */
export function getBuyerKnotVisibility(flagResults, userData) {
    return getKnotVisibility(flagResults, userData);
}

/** @param {'trade'|'deal'|'openKnots'} tab */
export function isKnotTabVisible(visibility, tab) {
    return !!visibility?.[tab];
}

export function hasAnyKnotTabAccess(visibility) {
    return !!(visibility?.trade || visibility?.deal || visibility?.openKnots);
}

/** @returns {'trade'|'deal'|'openKnots'|null} */
export function mapDashboardTypeToTab(type) {
    if (type === 'payable') return 'deal';
    if (type === 'openKnots') return 'openKnots';
    if (type === 'marketPlace' || type == null || type === '') return 'trade';
    return null;
}

export function isDashboardTypeAllowed(type, visibility) {
    const tab = mapDashboardTypeToTab(type);
    if (!tab) return false;
    return isKnotTabVisible(visibility, tab);
}

/** First enabled tab in banner order: Trade → Deal → Open Knots */
export function getDefaultKnotDashboardType(visibility) {
    if (isKnotTabVisible(visibility, 'trade')) return 'marketPlace';
    if (isKnotTabVisible(visibility, 'deal')) return 'payable';
    if (isKnotTabVisible(visibility, 'openKnots')) return 'openKnots';
    return null;
}

/** @deprecated Use getDefaultKnotDashboardType */
export function getDefaultBuyerDashboardType(visibility) {
    return getDefaultKnotDashboardType(visibility);
}

/**
 * @returns {null|string} null = stay on current URL; else ?type= for navigate
 */
export function resolveKnotDashboardRedirect(currentType, visibility, loaded) {
    if (!loaded || !hasAnyKnotTabAccess(visibility)) return null;
    if (isDashboardTypeAllowed(currentType, visibility)) return null;
    return getDefaultKnotDashboardType(visibility);
}

/** @deprecated Use resolveKnotDashboardRedirect */
export function resolveBuyerDashboardRedirect(currentType, visibility, loaded) {
    return resolveKnotDashboardRedirect(currentType, visibility, loaded);
}
